/**
 * Estate Locker Service
 *
 * Provides owner-facing operations for estate setup, settings,
 * final wishes, document links, and digital assets.
 * All queries rely on Supabase RLS for authorization enforcement.
 *
 * @module services/EstateLockerService
 */

import { supabase } from '@xanectech/supabase-client/supabase-client';
import { createLogger, getErrorMessage } from '@xanectech/lib';
import type { Database } from '@xanectech/types';
import { EstateLegacyContactService } from './EstateLegacyContactService';
import { EnhancedDataCategoryService } from './EnhancedDataCategoryService';

const logger = createLogger('EstateLockerService');

// ============================================
// Row type aliases
// ============================================

type EstateSettings =
  Database['public']['Tables']['estate_vault_settings']['Row'];
type EstateSettingsInsert =
  Database['public']['Tables']['estate_vault_settings']['Insert'];
type FinalWishes =
  Database['public']['Tables']['estate_vault_final_wishes']['Row'];
type FinalWishesInsert =
  Database['public']['Tables']['estate_vault_final_wishes']['Insert'];
type FinalWishesUpdate =
  Database['public']['Tables']['estate_vault_final_wishes']['Update'];
type DocumentLink =
  Database['public']['Tables']['estate_vault_document_links']['Row'];
type DigitalAsset =
  Database['public']['Tables']['estate_vault_digital_assets']['Row'];
type DigitalAssetInsert =
  Database['public']['Tables']['estate_vault_digital_assets']['Insert'];
type DigitalAssetUpdate =
  Database['public']['Tables']['estate_vault_digital_assets']['Update'];
type UserDocument = Database['public']['Tables']['user_documents']['Row'];
type DocumentLinkInsert =
  Database['public']['Tables']['estate_vault_document_links']['Insert'];

// ============================================
// DTOs
// ============================================

export interface EstateOverview {
  settings: EstateSettings | null;
  legacyContactCount: number;
  executorCount: number;
  wishesComplete: boolean;
  completionPercent: number;
  linkedDocumentCount: number;
  linkedPersonalDataCategoryCount: number;
}

// ============================================
// Service
// ============================================

export class EstateLockerService {
  /**
   * Return a summary overview for the estate dashboard.
   * Combines settings, counts, and completion status.
   */
  static async getEstateOverview(userId: string): Promise<EstateOverview> {
    await EstateLegacyContactService.syncLegacyContactsFromContactsCategory(
      userId,
    );

    const [settingsRes, legacyRes, executorRes, wishesRes, docLinksRes] =
      await Promise.all([
        supabase
          .from('estate_vault_settings')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle(),
        supabase
          .from('estate_vault_contacts')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('role', 'legacy_contact')
          .is('revoked_at', null),
        supabase
          .from('estate_vault_contacts')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('role', 'executor')
          .is('revoked_at', null),
        supabase
          .from('estate_vault_final_wishes')
          .select('is_complete')
          .eq('user_id', userId)
          .maybeSingle(),
        supabase
          .from('estate_vault_document_links')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId),
      ]);

    if (settingsRes.error) {
      logger.warn('Could not load estate settings for overview', {
        context: 'EstateLockerService',
      });
    }

    const legacyContactCount = legacyRes.count ?? 0;
    const executorCount = executorRes.count ?? 0;
    const wishesComplete = wishesRes.data?.is_complete ?? false;
    const linkedDocumentCount = docLinksRes.count ?? 0;

    // Count distinct categories that have at least one entry for this user
    let linkedPersonalDataCategoryCount = 0;
    try {
      const categories =
        await EnhancedDataCategoryService.getCategoriesWithEntryCounts(userId);
      linkedPersonalDataCategoryCount = categories.filter(
        (c) => c.entry_count > 0,
      ).length;
    } catch (error) {
      logger.warn('Could not load personal data category count for overview', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
    }

    // Simple completion: settings exist (25%), ≥1 legacy contact (25%), ≥1 executor (25%), wishes complete (25%)
    let completionPercent = 0;
    if (settingsRes.data) completionPercent += 25;
    if (legacyContactCount > 0) completionPercent += 25;
    if (executorCount > 0) completionPercent += 25;
    if (wishesComplete) completionPercent += 25;

    return {
      settings: settingsRes.data ?? null,
      legacyContactCount,
      executorCount,
      wishesComplete,
      completionPercent,
      linkedDocumentCount,
      linkedPersonalDataCategoryCount,
    };
  }

  /**
   * Fetch estate settings for the given user.
   */
  static async getEstateSettings(
    userId: string,
  ): Promise<EstateSettings | null> {
    const { data, error } = await supabase
      .from('estate_vault_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      logger.error('Failed to fetch estate settings', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_SETTINGS_FETCH_FAILED');
    }

    return data;
  }

  /**
   * Upsert estate settings for the given user.
   */
  static async upsertEstateSettings(
    userId: string,
    payload: Omit<EstateSettingsInsert, 'user_id'>,
  ): Promise<EstateSettings> {
    const { data, error } = await supabase
      .from('estate_vault_settings')
      .upsert({ ...payload, user_id: userId }, { onConflict: 'user_id' })
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to upsert estate settings', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_SETTINGS_UPSERT_FAILED');
    }

    return data;
  }

  /**
   * Fetch final wishes record for the given user.
   */
  static async getFinalWishes(userId: string): Promise<FinalWishes | null> {
    const { data, error } = await supabase
      .from('estate_vault_final_wishes')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      logger.error('Failed to fetch final wishes', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_WISHES_FETCH_FAILED');
    }

    return data;
  }

  /**
   * Upsert final wishes for the given user.
   * Encrypted fields must be pre-encrypted by caller if encryption is active.
   */
  static async upsertFinalWishes(
    userId: string,
    payload: Omit<FinalWishesInsert, 'user_id'>,
  ): Promise<FinalWishes> {
    const { data, error } = await supabase
      .from('estate_vault_final_wishes')
      .upsert({ ...payload, user_id: userId }, { onConflict: 'user_id' })
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to upsert final wishes', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_WISHES_UPSERT_FAILED');
    }

    return data;
  }

  /**
   * Mark final wishes as complete or incomplete.
   */
  static async setFinalWishesComplete(
    userId: string,
    isComplete: boolean,
  ): Promise<void> {
    const { error } = await supabase
      .from('estate_vault_final_wishes')
      .upsert(
        { user_id: userId, is_complete: isComplete },
        { onConflict: 'user_id' },
      );

    if (error) {
      logger.error('Failed to update final wishes completion flag', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_WISHES_COMPLETE_FAILED');
    }
  }

  /**
   * List document links for the given user's estate.
   */
  static async listEstateDocuments(userId: string): Promise<DocumentLink[]> {
    const { data, error } = await supabase
      .from('estate_vault_document_links')
      .select('*')
      .eq('user_id', userId)
      .order('linked_at', { ascending: false });

    if (error) {
      logger.error('Failed to list estate document links', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_DOCS_FETCH_FAILED');
    }

    return data ?? [];
  }

  /**
   * List all user documents available to link into the estate.
   * Excludes archived/deleted documents.
   */
  static async listUserDocuments(userId: string): Promise<UserDocument[]> {
    const { data, error } = await supabase
      .from('user_documents')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .is('archived_at', null)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Failed to list user documents', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('USER_DOCS_FETCH_FAILED');
    }

    return data ?? [];
  }

  /**
   * Link an existing user document into the estate vault.
   * Creates a link record only — the source document is never modified.
   * @param metadata.document_category  Optional document role (ContactDocRole)
   *   used for document workflow requirement matching. When provided, this
   *   value satisfies the matching requirement key across all contacts.
   */
  static async linkEstateDocument(
    userId: string,
    documentId: string,
    metadata?: Pick<
      DocumentLinkInsert,
      'document_category' | 'access_priority' | 'is_critical' | 'notes'
    >,
  ): Promise<DocumentLink> {
    const { data, error } = await supabase
      .from('estate_vault_document_links')
      .insert({
        user_id: userId,
        document_id: documentId,
        ...metadata,
      })
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to link document to estate', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_DOC_LINK_FAILED');
    }

    return data;
  }

  /**
   * Unlink a document from the estate vault.
   * Deletes only the link record — the source user_documents row is never touched.
   */
  static async unlinkEstateDocument(
    userId: string,
    linkId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from('estate_vault_document_links')
      .delete()
      .eq('id', linkId)
      .eq('user_id', userId);

    if (error) {
      logger.error('Failed to unlink document from estate', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_DOC_UNLINK_FAILED');
    }
  }

  /**
   * Return personal data categories with entry counts for the given user.
   * Used to display which categories have data available in estate scope.
   */
  static async getCategoriesForEstateScope(userId: string) {
    return EnhancedDataCategoryService.getCategoriesWithEntryCounts(userId);
  }

  /**
   * List digital assets for the given user.
   */
  static async listDigitalAssets(userId: string): Promise<DigitalAsset[]> {
    const { data, error } = await supabase
      .from('estate_vault_digital_assets')
      .select('*')
      .eq('user_id', userId)
      .order('display_order', { ascending: true });

    if (error) {
      logger.error('Failed to list digital assets', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_ASSETS_FETCH_FAILED');
    }

    return data ?? [];
  }

  /**
   * Create a digital asset record for the given user.
   */
  static async createDigitalAsset(
    userId: string,
    payload: Omit<DigitalAssetInsert, 'user_id'>,
  ): Promise<DigitalAsset> {
    const { data, error } = await supabase
      .from('estate_vault_digital_assets')
      .insert({ ...payload, user_id: userId })
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to create digital asset', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_ASSET_CREATE_FAILED');
    }

    return data;
  }

  /**
   * Update an existing digital asset.
   */
  static async updateDigitalAsset(
    userId: string,
    assetId: string,
    payload: DigitalAssetUpdate,
  ): Promise<DigitalAsset> {
    const { data, error } = await supabase
      .from('estate_vault_digital_assets')
      .update(payload)
      .eq('id', assetId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error || !data) {
      logger.error('Failed to update digital asset', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_ASSET_UPDATE_FAILED');
    }

    return data;
  }

  /**
   * Delete a digital asset.
   */
  static async deleteDigitalAsset(
    userId: string,
    assetId: string,
  ): Promise<void> {
    const { error } = await supabase
      .from('estate_vault_digital_assets')
      .delete()
      .eq('id', assetId)
      .eq('user_id', userId);

    if (error) {
      logger.error('Failed to delete digital asset', {
        context: 'EstateLockerService',
        error: getErrorMessage(error),
      });
      throw new Error('ESTATE_ASSET_DELETE_FAILED');
    }
  }
}

// Re-export row types for consumers
export type {
  EstateSettings,
  FinalWishes,
  FinalWishesInsert,
  FinalWishesUpdate,
  DocumentLink,
  DocumentLinkInsert,
  UserDocument,
  DigitalAsset,
  DigitalAssetInsert,
};
